import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Heart, Feather, Compass, Upload } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../hooks/use-toast';

const Home = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setIsUploading(true);
      // Simuler l'upload
      setTimeout(() => {
        toast({
          title: "Manuscrit reçu !",
          description: `Nous avons bien reçu "${file.name}". Nous vous contacterons sous peu.`,
        });
        setIsUploading(false);
      }, 1500);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Logo fixe en haut à gauche sur toutes les pages */}
      <div className="fixed top-24 left-8 z-40">
        <img 
          src="/logo-quid-novi.png" 
          alt="Quid Novi" 
          className="h-20 w-20"
        />
      </div>

      {/* Hero Section - Orange clair */}
      <section className="relative min-h-[80vh] flex items-center pt-24 bg-hermes-400">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-[2fr_auto] gap-16 items-center">
            {/* Contenu à gauche */}
            <div>
              <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6 leading-tight">
                {t.home.hero.title}
              </h1>
              <p className="text-2xl md:text-3xl text-white font-light mb-8 leading-relaxed">
                {t.home.hero.subtitle}
              </p>
              <div className="text-lg md:text-xl text-white/95 leading-relaxed space-y-4 max-w-3xl">
                {t.home.hero.description.split('\n\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </div>

            {/* Logo grand à droite du texte */}
            <div className="hidden lg:block">
              <img 
                src="/logo-quid-novi.png" 
                alt="Quid Novi" 
                className="h-64 w-64 opacity-90"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Ligne blanche de séparation */}
      <div className="h-1 bg-white"></div>

      {/* Qui sommes-nous ? - Orange foncé */}
      <section id="qui-sommes-nous" className="py-32 px-4 sm:px-6 lg:px-8 bg-hermes-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-serif font-bold text-white mb-12 leading-tight">
            {t.home.whoweare.title}
          </h2>
          <p className="text-2xl text-white/95 leading-relaxed">
            {t.home.whoweare.content}
          </p>
        </div>
      </section>

      {/* Ligne blanche de séparation */}
      <div className="h-1 bg-white"></div>

      {/* Nos Valeurs - Orange clair */}
      <section id="valeurs" className="py-32 px-4 sm:px-6 lg:px-8 bg-hermes-400">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-serif font-bold text-white mb-4">
              {t.home.values.title}
            </h2>
            <div className="w-32 h-1 bg-white mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {t.home.values.items.map((value, index) => {
              const icons = {
                book: BookOpen,
                heart: Heart,
                feather: Feather,
                compass: Compass
              };
              const Icon = icons[value.icon];
              
              return (
                <div
                  key={index}
                  className="bg-white/10 backdrop-blur-sm p-10 transition-all hover:bg-white/20 border-l-4 border-white"
                >
                  <div className="mb-6">
                    <Icon className="w-12 h-12 text-white" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-2xl font-serif font-semibold text-white mb-4">
                    {value.title}
                  </h3>
                  <p className="text-lg text-white/90 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ligne blanche de séparation */}
      <div className="h-1 bg-white"></div>

      {/* Collections avec nouvelles images - Orange foncé */}
      <section id="collections" className="py-32 px-4 sm:px-6 lg:px-8 bg-hermes-600">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-serif font-bold text-white mb-6">
              {t.home.collections.title}
            </h2>
            <p className="text-xl text-white/90">
              {t.home.collections.subtitle}
            </p>
            <div className="w-32 h-1 bg-white mx-auto mt-6"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.home.collections.items.map((collection, index) => (
              <div
                key={index}
                className="group bg-white overflow-hidden transition-all hover:scale-105 shadow-2xl"
              >
                <div className="aspect-[3/4] overflow-hidden">
                  <img 
                    src={collection.image}
                    alt={collection.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 bg-white">
                  <h3 className="text-2xl font-serif font-semibold text-hermes-800 mb-3">
                    {collection.name}
                  </h3>
                  <p className="text-stone-700 leading-relaxed">
                    {collection.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ligne blanche de séparation */}
      <div className="h-1 bg-white"></div>

      {/* CTA Devenir auteur avec upload - Orange clair */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 bg-hermes-400">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-serif font-bold text-white mb-8 leading-tight">
            {t.home.cta.title}
          </h2>
          <p className="text-2xl text-white/95 mb-12 leading-relaxed">
            {t.home.cta.subtitle}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            {/* Bouton upload manuscrit */}
            <label className="cursor-pointer inline-flex items-center space-x-3 px-12 py-5 bg-white text-hermes-700 text-xl font-semibold rounded-none hover:bg-stone-100 transition-all shadow-2xl">
              <Upload className="w-6 h-6" />
              <span>{isUploading ? 'Envoi...' : t.home.cta.button}</span>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleFileUpload}
                className="hidden"
                disabled={isUploading}
              />
            </label>

            {/* Bouton contact */}
            <Link
              to="/contact"
              className="inline-flex items-center space-x-2 px-12 py-5 border-2 border-white text-white text-xl font-semibold rounded-none hover:bg-white/10 transition-all"
            >
              <span>Nous contacter</span>
            </Link>
          </div>

          <p className="text-sm text-white/70 mt-6">
            Formats acceptés : PDF, DOC, DOCX
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;